/*******************************************************************************
* Licensed Materials - Property of HCL 
* (c) Copyright HCL Technologies Ltd. 2020. All Rights Reserved. 

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/
/**
 * Component that create a BarWidget with labels
 * required params:
 *  - data: object with data matrix and labels
 *  Example : {
 *      values: [
            [0.5, 0.6, 0.7, 0.8, 0.6],
            [0.6, 0.7, 0.8, 0.8, 0.7],
            [0.4, 0.5, 0.6, 0.7, 0.8],
            [0.8, 0.9, 1, 0.9, 0.9],
            [1.1, 1.2, 0.9, 1, 1],
            [1.2, 1.4, 1.6, 1.7, 1.2],
        ],
        zLabels: [
            "Workstation1",
            "Workstation2",
            "Workstation3",
            "Workstation4",
            "Workstation5",
        ],
        xLabels: ["7:00", "8:00", "9:00", "10:00", "11:00", "12:00"],
        colors: ["#71d9f3", "#c7d540", "#ffadef", "#41e287", "#ffdf41"],

    }
 */

import React, { useState, Fragment } from "react";
import PropTypes from "prop-types";
import Panel from "../GUI/Panel";
import BackgroundPanel from "../GUI/BackgroundPanel";
import Text from "../GUI/Text";
import "../../aframe_components/WatchMe";

const BarWidget = React.memo(({
    aframeClasses,
    data,
    fontColor = "#c6c6c6",
    box = false,
    boxColor = "#5c5c5c",
    gridLinesColor = "#474747",
    clue,
    ...otherAttributes
}) => {
    const [chartInfo, setChartInfo] = useState([]);
    if (!data.colors)
        data = {
            ...data,
            colors: [
                "#71d9f3",
                "#c7d540",
                "#ffadef",
                "#41e287",
                "#ffdf41"
            ]
        }
    const draw = () => {
        return data.values
            .map((value, index) => {
                return value.map((v, i) => {
                    const key = `${index} ${i}`;
                    if (v)
                        return (
                            <a-entity
                                key={key}
                                position={`${i} 0 ${index}`}
                            >
                                <a-box
                                    class={aframeClasses}
                                    width={size / 2}
                                    depth={size / 2}
                                    height={v * scale.y}
                                    color={
                                        data.colors[
                                        index % data.colors.length
                                        ]
                                    }
                                    position={`0 ${(v * scale.y) / 2} 0`}
                                    animation__mouseenter={`property: scale; startEvents: mouseenter; to: ${1.1} ${1 + 0.1 / (v * scale.y)
                                        } ${1.1}; dur: 150`}
                                    animation__mouseleave="property: scale; startEvents: mouseleave; to: 1 1 1; dur: 150"
                                    onClick={(e) => {
                                        setChartInfo((oldChartInfo) => {
                                            return !oldChartInfo.includes(
                                                key
                                            )
                                                ? [...oldChartInfo, key]
                                                : oldChartInfo.filter(
                                                    (x) => x !== key
                                                );
                                        });
                                    }}
                                />
                                {chartInfo.includes(key) && (
                                    <Panel
                                        watch-me
                                        panel
                                        width={size * 1.5}
                                        height={size}
                                        aframeClasses={aframeClasses}
                                        position={`0 ${v * scale.y + size
                                            } 0`}
                                        opacity={1}
                                        color="#262626"
                                    >
                                        <Text
                                            width={size * 1.5}
                                            text={`${v}`}
                                            align="center"
                                            color={fontColor}
                                            sizeDecimal={4 / (size * 1.5)}
                                        />
                                        <Text
                                            width={size * 1.5}
                                            text={`${v}`}
                                            align="center"
                                            color={fontColor}
                                            rotation="0 180 0"
                                            sizeDecimal={4 / (size * 1.5)}
                                            position="0 0 -0.001"
                                        />
                                    </Panel>
                                )}
                            </a-entity>
                        );
                    return null;
                });
            })
            .flat(1);
    };

    const drawGrid = (panelWidth, panelHeight, numTick, base) => {
        const clueAxis =
            clue &&
            clue.threshold &&
            Object.keys(data.axis).find(
                (key) => data.axis[key] === clue.axe
            );

        const threshold = clueAxis && parseFloat(clue.threshold) * scale.y;

        const clueLine =
            clueAxis &&
                ((clueAxis === "y" && !base && threshold <= panelHeight) ||
                    (clueAxis === "z" && base && threshold <= panelHeight) ||
                    (clueAxis === "x" && base && threshold <= panelWidth)) ? (
                <a-plane
                    key={`clue${panelHeight}-${panelWidth}-${base}`}
                    color="#f00"
                    height={
                        "zy".includes(clueAxis) ? size / 20 : panelHeight
                    }
                    width={"zy".includes(clueAxis) ? panelWidth : size / 20}
                    position={`${clueAxis === "x"
                        ? panelWidth / 2 - threshold + size / 2
                        : 0
                        } ${"zy".includes(clueAxis)
                            ? -panelHeight / 2 +
                            threshold -
                            (base ? size / 2 : 0)
                            : 0
                        } 0.01`}
                />
            ) : null;

        const grid = new Array(Math.ceil(numTick))
            .fill(0)
            .map((element, index) => {
                return (
                    <a-plane
                        key={`vertical${index}`}
                        color={gridLinesColor}
                        height={panelHeight}
                        width={size / 20}
                        position={`${-panelWidth / 2 + index + size / 2
                            } 0 0.01`}
                    />
                );
            })
            .concat(
                new Array(Math.ceil(panelHeight))
                    .fill(0)
                    .map((elem, index) => {
                        return (
                            <a-plane
                                key={`horizontal${index}`}
                                color={gridLinesColor}
                                height={size / 20}
                                width={panelWidth}
                                position={`0 ${-panelHeight / 2 +
                                    index +
                                    (base ? size / 2 : 0)
                                    } 0.01`}
                            />
                        );
                    })
            );

        if (clueLine) grid.push(clueLine);
        return grid;
    };

    // Takes labels and return an array of Text ready to be renderized by React

    const createLabels = () => {
        const { zLabels, xLabels } = data;
        if (!xLabels.length || !zLabels.length) return null;
        const zMax = zLabels.length;
        const maxWord = Math.max(...zLabels.map((x) => x.length));
        const maxWordX = Math.max(...xLabels.map((x) => `${x}`.length));

        return zLabels
            .map((value, index) => {
                return (
                    <Text
                        key={`z${index}`}
                        position={`-1 0.2 ${index}`}
                        text={value.toString()}
                        width={5}
                        side="double"
                        align="right"
                        sizeDecimal={maxWord > 4 ? 4 : maxWord}
                        color={fontColor}
                    />
                );
            })
            .concat(
                xLabels.map((value, index) => {
                    return (
                        <Text
                            key={`x${index}`}
                            position={`${index} 0.2 ${zMax || 1}`}
                            text={value.toString()}
                            width={1.2}
                            side="double"
                            align="center"
                            sizeDecimal={(maxWordX > 4 ? 4 : maxWordX) + 1}
                            color={fontColor}
                        />
                    );
                })
            )
            .concat(
                new Array(Math.ceil(yMax + 1)).fill(0).map((y, i) => {
                    if (!i) return null;
                    return (
                        <Text
                            key={`y${i}`}
                            position={`-0.5 ${i} -0.5`}
                            text={
                                Number.isInteger(i / scale.y)
                                    ? (i / scale.y).toFixed(0)
                                    : (i / scale.y).toFixed(1)
                            }
                            width={3}
                            side="double"
                            align="right"
                            sizeDecimal={3}
                            color={fontColor}
                        />
                    );
                })
            );
    };

    if (!data) return null;

    const xMax = data.values[0] && data.values[0].length;
    const zMax = data.values.length;
    let yMax = Math.max(...data.values.flat(1));
    if (!xMax || !zMax || !yMax) return null;

    let scale = { x: 1, y: 1, z: 1 };
    scale.y = xMax / 2 / yMax;
    yMax = xMax / 2;

    const size = 1;

    return (
        <a-entity class="barWidget" {...otherAttributes}>
            <a-entity
                position={`${-xMax / 2 + size / 2} 0.01 ${-zMax / 2 + size / 2
                    }`}
            >
                {draw()}
                {createLabels()}
            </a-entity>
            {box && (
                <>
                    {(data.xLabels.length || data.zLabels.length) && (
                        <>
                            <a-plane
                                color="green"
                                height={yMax + 1}
                                width={size / 10}
                                position={`${-xMax / 2} ${(yMax + 1) / 2} ${-zMax / 2
                                    }`}
                                side="double"
                            />
                            <a-plane
                                color="red"
                                height={size / 10}
                                width={xMax}
                                position={`0 ${size / 20} ${-zMax / 2 + 0.001
                                    }`}
                                side="double"
                            />
                            <a-plane
                                color="blue"
                                height={size / 10}
                                width={zMax}
                                position={`${-xMax / 2} 0 0`}
                                rotation="0 -90 0"
                                side="double"
                            />
                        </>
                    )}
                    <BackgroundPanel
                        class={aframeClasses}
                        width={xMax}
                        height={zMax}
                        rotation="-90 180 0"
                        color={boxColor}
                        opacity="0.5"
                    >
                        {drawGrid(xMax, zMax, xMax, true)}
                    </BackgroundPanel>
                    <BackgroundPanel
                        width={zMax}
                        height={yMax + 1}
                        position={`${-xMax / 2} ${(yMax + 1) / 2} 0`}
                        rotation="0 90 0"
                        side="front"
                        color={boxColor}
                        opacity="0.5"
                    >
                        {drawGrid(zMax, yMax + 1, zMax, false)}
                    </BackgroundPanel>
                    <BackgroundPanel
                        width={zMax}
                        height={yMax + 1}
                        position={`${xMax / 2} ${(yMax + 1) / 2} 0`}
                        rotation="0 -90 0"
                        side="front"
                        color={boxColor}
                        opacity="0.5"
                    >
                        {drawGrid(zMax, yMax + 1, zMax, false)}
                    </BackgroundPanel>
                    <BackgroundPanel
                        width={xMax}
                        height={yMax + 1}
                        position={`0 ${(yMax + 1) / 2} ${-zMax / 2}`}
                        side="front"
                        color={boxColor}
                        opacity="0.5"
                    >
                        {drawGrid(xMax, yMax + 1, xMax, false)}
                    </BackgroundPanel>
                    <BackgroundPanel
                        width={xMax}
                        height={yMax + 1}
                        position={`0 ${(yMax + 1) / 2} ${zMax / 2}`}
                        rotation="0 180 0"
                        side="front"
                        color={boxColor}
                        opacity="0.5"
                    >
                        {drawGrid(xMax, yMax + 1, xMax, false)}
                    </BackgroundPanel>
                </>
            )}
        </a-entity>
    );
});

// requiring data
BarWidget.propTypes = {
    data: PropTypes.object
};

export default BarWidget;
